<template>
  <div>
    <v-container class="container">
      <div v-if="isHandlingLogout" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="64" />
        <div class="mt-4">
          <h3>Signing you out...</h3>

          <p>Please wait while we complete your logout process.</p>
        </div>
      </div>
      <error-component v-else :message="error" v-on:clear="clear()" />
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import { Logger } from '../mixins/logger';
import { AjaxLoader } from '../mixins/ajaxLoader';
import { Helper } from '../mixins/helpers.js';
import { decodeState } from '../util/state-utils.js';
import { captureMessage } from '@/services/sentry.service';
import { usePathwaysStore } from '@/stores/pathways';
import { useUserStore } from '@/stores/user';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';

export default defineComponent({
  name: 'CallbackView',

  data() {
    return {
      activeLoading: false,
      error: '',
      isHandlingLogout: false,
      isBeingDestroyed: false,
      timeoutIds: [], // Track timeout IDs for cleanup
      abortController: null // For cancelling fetch requests
    };
  },

  components: {},
  mixins: [Logger, AjaxLoader, Helper],

  methods: {
    async handleLoginEvent(session) {
      this.logInfo('handle login event', session);

      let self = this;

      if (this.$route.query.pathwayId) {
        const pathwaysStore = usePathwaysStore();
        pathwaysStore.setPathwayId(this.$route.query.pathwayId);
      }

      // Parse session.state, check if it contains data of format '{"redirect_to":{"name":"courses-history"},"learnerId":"239752"}'
      await this.handleSessionState(session);

      // Handle persistent parameters restoration from auth state
      this.logInfo('About to restore persistent params from session state');

      await this.restorePersistentParamsFromState(session);

      this.logInfo('init loggedin session in callback');

      try {
        const userInfo = await this.$conn.initSecureSession(true);

        this.logInfo('init loggedin session success', userInfo);

        if (!userInfo) {
          this.logError('userInfo is undefined after initSecureSession');

          // Check if this is due to aether-link or aether-token redirect (connector.service.js triggers auth.login and returns undefined)
          const aetherLinkContext = sessionStorage.getItem('aetherLinkFlow');
          const aetherTokenContext = sessionStorage.getItem('aetherTokenFlow');
          if (aetherLinkContext || aetherTokenContext) {
            return;
          }

          this.$router.push({ name: 'WelcomeDesktop' });
          return;
        }

        // Check if ASURITE user needs Aether verification
        const authProviderType = this.$auth.getActiveProviderType();
        const isAsuriteUser = authProviderType === 'asurite';
        const hasAetherId = !!this.$conn.learnerData?.emailotpCustomOauthId;

        // Check for return from aether-link flow
        const aetherLinkContext = sessionStorage.getItem('aetherLinkFlow');

        if (aetherLinkContext && authProviderType === 'custom_oauth') {
          // Returning from Aether OTP verification - link the ID
          const context = JSON.parse(aetherLinkContext);
          const aetherId = this.$auth.getUserId();

          try {
            await this.$conn.linkAetherIdToLearner(context.learnerId, aetherId);

            // Continue with Custom OAuth session (don't try to restore ASURITE - it's no longer valid)
            // The user now has their Aether ID linked and can continue using the app
            sessionStorage.removeItem('aetherLinkFlow');
            this.$router.push({ name: context.returnTo || 'dashboard' });
            return;
          } catch (error) {
            this.logError('Failed to link Aether ID', error);
            // Learner stays unlinked, so the next login sends another code.
            captureMessage('Aether id link failed after OTP verification', {
              level: 'warning',
              context: {
                tags: { aetherLink: 'failed', otpTrigger: 'callback-aether-link' },
                extra: { learnerId: context.learnerId, errorMessage: error?.message }
              }
            });

            sessionStorage.removeItem('aetherLinkFlow');
            // Continue to dashboard anyway - user can retry later
          }
        }

        // Handle return from aether-token flow (user already has Aether ID, just needed custom_oauth token)
        const aetherTokenContext = sessionStorage.getItem('aetherTokenFlow');
        if (aetherTokenContext && authProviderType === 'custom_oauth') {
          sessionStorage.removeItem('aetherTokenFlow');
          const context = JSON.parse(aetherTokenContext);
          this.$router.push({ name: context.returnTo || 'dashboard' });
          return;
        }

        // Redirect ASURITE users without Aether ID to verification flow (only when Aether flow enabled)
        if (userInfo.useAetherFlow && isAsuriteUser && !hasAetherId && userInfo.eaOptIn?.toLowerCase() !== 'false') {
          const flowContext = {
            email: userInfo.emailId,
            learnerId: this.$conn.learnerData?.learnerId,
            returnTo: 'dashboard'
          };
          sessionStorage.setItem('aetherLinkFlow', JSON.stringify(flowContext));

          // This branch skips the ASURITE bypass entirely, so it always mails a code.
          captureMessage('Unrequested OTP: ASURITE bypass fell back to email OTP', {
            level: 'warning',
            context: {
              tags: {
                otpFallback: 'sent',
                otpTrigger: 'callback-aether-link',
                otpFallbackReason: 'bypass-not-attempted'
              },
              extra: { learnerId: this.$conn.learnerData?.learnerId }
            }
          });

          // Directly trigger Custom OAuth login for Aether ID verification
          this.$auth.login('custom_oauth', { email: userInfo.emailId });
          return;
        }

        if (userInfo.eaOptIn && userInfo.eaOptIn.toLowerCase() === 'false') {
          this.$conn.setLearnerSub();

          this.$session.set('fromCallBack', true);

          this.$router.push({ name: 'Registerv2' });
        } else {
          let currentTab = this.getItemFromLocalStorage('requestedURL');

          // If we have a requestedURL, redirect directly to it (Vue 2 approach)
          if (currentTab) {
            localStorage.removeItem('requestedURL');
            localStorage.removeItem('login_state');

            window.location.href = currentTab;

            return;
          }

          const dashboardRoute = {
            name: 'dashboard',
            query: this.$route.query || {}
          };
          const pathwaysStore = usePathwaysStore();
          const userStore = useUserStore();
          if (pathwaysStore.pathwayId || userStore.getLearnerData._partnerName === 'Pathways for the Future Learners') {
            // Redirect to dashboard - pathway components are now on dashboard
            self.$router.push(dashboardRoute);
          } else {
            // eslint-disable-next-line no-console
            var _hsq = (window._hsq = window._hsq || []);
            _hsq.push([
              'identify',
              {
                email: this.$conn.learnerData._emailId
              }
            ]);
            _hsq.push(['trackPageView']);
            localStorage.removeItem('login_state');

            self.$router.push(dashboardRoute);
          }
        }
      } catch (err) {
        this.logError('init loggedin session failed', err);

        // Note: NO_CONTACT_FOUND is now handled as 200 response in connector.service.js
        // No longer handling NO_CONTACT_FOUND in catch block since it's not an error anymore
        this.error =
          typeof ((err.response || {}).data || {}).message === 'string'
            ? (JSON.parse(((err.response || {}).data || {}).message) || {}).message
            : ((err.response || {}).data || {}).message || '';

        this.logError('init loggedin session failed', err);
      } finally {
      }
    },

    async handleSessionState(session) {
      try {
        let stateData;

        // Check if session contains state data
        if (session && session.state) {
          // Parse state data - it could be a string or already parsed object
          if (typeof session.state === 'string') {
            try {
              this.logInfo('Parsing session state as string', session.state);

              stateData = decodeState(session.state);

              this.logInfo('Parsed session state as JSON', stateData);
            } catch (parseError) {
              this.logInfo('State is not valid encoded state, treating as plain string:', session.state);
              return;
            }
          } else if (typeof session.state === 'object') {
            stateData = session.state;
          } else {
            this.logInfo('State data is not string or object:', session.state);
            return;
          }
        }

        // If no valid state from session, check localStorage for our encoded state
        if (!stateData) {
          const encodedState = localStorage.getItem('login_state');
          if (encodedState) {
            try {
              stateData = decodeState(encodedState);

              this.logInfo('Retrieved and decoded state from localStorage', stateData);
            } catch (error) {
              this.logError('Failed to decode localStorage state:', error);
            }
          }
        }

        if (!stateData) {
          this.logInfo('No state data found');
          return;
        }

        // Check if state contains learnerId for patching learner info
        if (stateData.learnerId) {
          this.logInfo('Found learnerId in state, calling patchLearnersInfo', {
            learnerId: stateData.learnerId
          });

          try {
            // Get idToken payload from session.authResult.idToken
            let idTokenPayload = null;
            if (session.authResult && session.authResult.idToken) {
              if (typeof session.authResult.idToken === 'object') {
                this.logInfo('Calling patchLearnersInfo with idTokenPayload', idTokenPayload);

                const response = await this.$conn.patchLearnersInfo({}, stateData.learnerId);

                // If we get a 200 response and have redirect_to, redirect to that page
                if (response && stateData.redirect_to) {
                  this.logInfo('patchLearnersInfo successful, redirecting to:', stateData.redirect_to);

                  // Handle different redirect_to formats
                  if (typeof stateData.redirect_to.name === 'string') {
                    window.location.href = `/${stateData.redirect_to.name}`;
                  }
                } else {
                  this.logInfo('patchLearnersInfo completed successfully');
                }
                return; // Exit early to avoid normal flow
              }
            }
          } catch (patchError) {
            this.logError('Failed to patch learner info:', patchError);
          }
        }
      } catch (error) {
        this.logError('Error handling session state:', error);
      }
    },

    /**
     * Helper method to handle route navigation with persistent parameters from state
     */
    async restorePersistentParamsFromState(session) {
      try {
        let stateData;

        // Define persistent parameters to handle
        const persistentParams = ['viewAs', 'course_offering_id'];

        // Parse state data to extract persistent parameters
        if (session && session.state) {
          if (typeof session.state === 'string') {
            try {
              stateData = decodeState(session.state);
              this.logInfo('Parsed session state for persistent params restoration:', stateData);
            } catch (parseError) {
              this.logInfo('State is not valid encoded state for persistent params, skipping restoration');
            }
          } else if (typeof session.state === 'object') {
            stateData = session.state;
          }
        }

        // If no valid state from session, check localStorage
        if (!stateData) {
          const encodedState = localStorage.getItem('login_state');
          if (encodedState) {
            try {
              stateData = decodeState(encodedState);
              this.logInfo('Retrieved persistent params state from localStorage:', stateData);
            } catch (error) {
              this.logError('Failed to decode localStorage state for persistent params:', error);
            }
          }
        }

        if (!stateData) {
          this.logInfo('No state data found for persistent params restoration');
          return;
        }

        // Check if state contains any persistent parameters
        const foundParams = {};
        let hasAnyParam = false;

        persistentParams.forEach((paramName) => {
          if (stateData[paramName]) {
            foundParams[paramName] = stateData[paramName];
            hasAnyParam = true;
          }
        });

        if (hasAnyParam) {
          this.logInfo('Found persistent params in auth state:', foundParams);

          // Get the current route name
          const currentRoute = this.$router.currentRoute.value;
          const targetRouteName = currentRoute.name || 'dashboard';

          // Build new URL with persistent parameters
          const routeQuery = { ...currentRoute.query };
          Object.entries(foundParams).forEach(([paramName, value]) => {
            routeQuery[paramName] = value;
          });

          this.logInfo('Redirecting to route with persistent params:', {
            name: targetRouteName,
            query: routeQuery
          });

          // Navigate to the route with persistent parameters
          this.$router.replace({
            name: targetRouteName,
            query: routeQuery
          });
        } else {
          this.logInfo('No persistent params found in auth state');
        }
      } catch (error) {
        this.logError('Error restoring persistent params from state:', error);
      }
    },

    clear() {
      this.$router.push({ name: 'login' });
    },

    sleep(ms) {
      return new Promise((res) => {
        setTimeout(() => res(), ms);
      });
    },

    async handleLogoutCallback() {
      this.isHandlingLogout = true;
      this.logInfo('handling logout callback');

      try {
        // Handle post-logout callback through the auth service
        const logoutHandled = this.$auth.handlePostLogoutCallback();

        if (logoutHandled) {
          this.logInfo('logout callback handled successfully');

          // Clear any remaining session data
          this.$session.clear();

          // Redirect to main page
          window.location.href = '/';
        } else {
          this.logError('logout callback validation failed');
          window.location.href = '/';
        }
      } catch (err) {
        this.logError('error handling logout callback', err);

        window.location.href = '/';
      } finally {
        this.isHandlingLogout = false;
      }
    },

    isLogoutCallback() {
      // Check if this is a logout callback by looking for logout-specific parameters
      const urlParams = new URLSearchParams(window.location.search);
      const state = urlParams.get('state');
      const logoutInProgress = sessionStorage.getItem('logout_in_progress');
      const storedLogoutState = sessionStorage.getItem('logout_state');

      // Check if we're on the logout path (handle both /logout and /logout/)
      const isLogoutPath = window.location.pathname === '/logout' || window.location.pathname === '/logout/';

      // Check if we have logout state or logout was in progress or on logout path
      return logoutInProgress || (state && storedLogoutState === state) || isLogoutPath;
    },

    handleAuthError(err) {
      if (!err) return;

      this.logError('handle auth', err);

      // Handle OAuth server errors with friendly error page
      if (err.type === 'OAUTH_SERVER_ERROR') {
        if (!this.isBeingDestroyed && !this._isDestroyed) {
          this.$router.push({
            path: '/error-page',
            query: {
              type: 'oauth',
              error: err.message,
              message: err.friendlyMessage
            }
          });
        }
        return;
      }

      // Handle missing authorization code error
      if (err.message && err.message.includes('No authorization code found in callback URL')) {
        this.logError('Missing authorization code - possible OAuth flow issue', {
          currentUrl: window.location.href,
          error: err.message
        });

        // Set a more user-friendly error message
        this.error = 'Authentication failed. Please try logging in again.';
        return;
      }

      // Handle OAuth flow interruption
      if (err.type === 'OAUTH_FLOW_INTERRUPTED') {
        this.logError('OAuth flow was interrupted', {
          currentUrl: window.location.href,
          error: err.message
        });

        // Automatically redirect to login after a short delay
        this.error = 'Login session expired. Redirecting to login page...';
        const timeoutId = setTimeout(() => {
          if (!this.isBeingDestroyed && !this._isDestroyed) {
            this.$router.push({ name: 'login' });
          }
        }, 3000);
        this.timeoutIds.push(timeoutId);
        return;
      }

      // Handle logout callback misidentification
      if (err.message && err.message.includes('This appears to be a logout callback')) {
        this.logInfo('Detected logout callback, redirecting appropriately');
        this.handleLogoutCallback();
        return;
      }

      if (err.errorDescription === 'The ASURITE affiliation appears to be incorrectly setup') {
        const uiStore = useUiStore();
        uiStore.setGlobalError({
          ...uiStore.globalError,
          message: `Oops! It appears that your ASURITE User ID is not ready for you to sign in with yet. It can take up to 24 hours for your ID to be fully functional after you've activated it. Please <a href="${
            Helper.methods.isUlcPartner() ? 'https://ulc.asu.edu/contact/' : 'https://ea.asu.edu/contact/'
          }" target="_blank">contact our support team</a> if you are unable to log in after 24 hours.`
        });
      }

      // For other errors, set a generic error message
      if (!this.error) {
        this.error = 'Authentication failed. Please try logging in again.';
      }

      // Only redirect to home if no specific error handling was done and component is still mounted
      if (!this.error && !this.isBeingDestroyed && !this._isDestroyed) {
        this.$router.push('/');
      }
    }
  },

  created() {
    try {
      this.logInfo('handle auth');

      // Initialize abort controller for cleanup
      this.abortController = new AbortController();

      // Check if this is a logout callback first
      if (this.isLogoutCallback()) {
        this.logInfo('detected logout callback');
        this.handleLogoutCallback();
        return;
      }

      // Regular login callback handling
      this.$auth
        .parseCallbackAccessToken()
        .then((res) => {
          // Check if component is still mounted before proceeding
          if (this.isBeingDestroyed || this._isDestroyed) {
            return;
          }
          this.logInfo('handle auth', res);
        })
        .catch((err) => {
          // Check if component is still mounted before proceeding
          if (this.isBeingDestroyed || this._isDestroyed) {
            return;
          }

          this.handleAuthError(err);
        });
    } catch (error) {
      this.logError('Error in created hook', error);
      this.error = 'An unexpected error occurred during authentication.';
    }
  },

  beforeUnmount() {
    // Mark component as being destroyed to prevent further operations
    this.isBeingDestroyed = true;
  },

  unmounted() {
    // Clean up any pending operations
    this.logInfo('Callback component unmounted');
  }
});
</script>
